import { useEffect, useState } from 'react';
import { DataError, loadReport } from '../lib/data.js';
import { EmptyState, Panel } from './Panel.js';

/**
 * A deliberately small Markdown renderer. The reports are generated by this
 * project, so the subset they use is known exactly: headings, lists, bold,
 * inline code, links and a rule. Pulling in a full Markdown parser to render
 * our own output would be weight for no benefit.
 */
function renderMarkdown(md: string): React.JSX.Element[] {
  const out: React.JSX.Element[] = [];
  let list: string[] = [];
  let key = 0;

  const flushList = (): void => {
    if (list.length === 0) return;
    out.push(
      <ul key={`ul-${key++}`}>
        {list.map((item, i) => (
          <li key={i}>{inline(item)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  for (const raw of md.split('\n')) {
    const line = raw.trimEnd();
    if (line.startsWith('- ')) {
      list.push(line.slice(2));
      continue;
    }
    flushList();
    if (line === '') continue;
    if (line === '---') {
      out.push(<hr key={`hr-${key++}`} />);
    } else if (line.startsWith('## ')) {
      out.push(<h2 key={`h2-${key++}`}>{inline(line.slice(3))}</h2>);
    } else if (line.startsWith('# ')) {
      out.push(<h1 key={`h1-${key++}`}>{inline(line.slice(2))}</h1>);
    } else if (line.startsWith('> ')) {
      out.push(
        <blockquote
          key={`bq-${key++}`}
          style={{
            margin: '0.75rem 0',
            paddingLeft: '0.9rem',
            borderLeft: '3px solid var(--warning)',
          }}
        >
          {inline(line.slice(2))}
        </blockquote>,
      );
    } else {
      out.push(<p key={`p-${key++}`}>{inline(line)}</p>);
    }
  }
  flushList();
  return out;
}

function inline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index;
    if (index > last) nodes.push(text.slice(last, index));
    const token = match[0];
    if (token.startsWith('**')) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      nodes.push(<code key={key++}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith('[')) {
      const link = /\[([^\]]+)\]\(([^)]+)\)/.exec(token);
      nodes.push(
        link ? (
          <a key={key++} href={link[2]}>
            {link[1]}
          </a>
        ) : (
          token
        ),
      );
    } else {
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    last = index + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Reports({ dates }: { dates: string[] }): React.JSX.Element {
  const [selected, setSelected] = useState(dates[0] ?? '');
  const [body, setBody] = useState<string | undefined>(undefined);
  const [error, setError] = useState<DataError | undefined>(undefined);

  useEffect(() => {
    if (selected === '') return;
    let cancelled = false;
    setBody(undefined);
    setError(undefined);
    loadReport(selected)
      .then((text) => {
        if (!cancelled) setBody(text);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof DataError
              ? e
              : new DataError('The report could not be loaded.', 'Pick another date.'),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selected]);

  if (dates.length === 0) {
    return (
      <Panel title="Daily reports" lede="What changed, in prose.">
        <EmptyState headline="No reports have been generated yet.">
          <p style={{ margin: 0 }}>
            Generate one with <code>observatory report</code> after a crawl and an aggregate.
          </p>
        </EmptyState>
      </Panel>
    );
  }

  return (
    <Panel
      title="Daily reports"
      lede="What changed, in prose, written for a reader with no context."
    >
      <div className="controls">
        <div className="field">
          <label htmlFor="rp-date">Report</label>
          <select
            id="rp-date"
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
            }}
          >
            {dates.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error !== undefined && (
        <EmptyState headline={error.what}>
          <p style={{ margin: 0 }}>{error.remedy}</p>
        </EmptyState>
      )}

      {body !== undefined && <article className="report">{renderMarkdown(body)}</article>}
    </Panel>
  );
}
